import React, { useState, useEffect, useCallback } from 'react';
import {
    Box,
    Typography,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    CircularProgress,
    Button,
    Stack,
    IconButton,
    Tooltip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
} from '@mui/material';
import { Plus, Trash2, SquarePen } from 'lucide-react';
import { getReviewQuestions, createReviewQuestion, updateReviewQuestion, deleteReviewQuestion } from '../../Services/ReviewManagement/ReviewApis';
import DeleteModal from '../../Components/Modals/DeleteModal';
import PermissionWrapper from '../../Components/Auth/PermissionWrapper';
import { useGlobalSnackbar } from '../../Components/Common/GlobalSanckbar';

const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return '-';
    return date.toLocaleDateString('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric'
    });
};

const ReviewQuestions = () => {
    const { showSnackbar } = useGlobalSnackbar();
    const [questions, setQuestions] = useState([]);
    const [loading, setLoading] = useState(true);

    // Modal states
    const [openModal, setOpenModal] = useState(false);
    const [isEdit, setIsEdit] = useState(false);
    const [formData, setFormData] = useState({
        question: '',
        example: ''
    });
    const [currentItem, setCurrentItem] = useState(null);
    const [submitting, setSubmitting] = useState(false);
    const [fieldErrors, setFieldErrors] = useState({});

    // Delete modal
    const [deleteModalOpen, setDeleteModalOpen] = useState(false);
    const [selectedId, setSelectedId] = useState(null);
    const [isDeleting, setIsDeleting] = useState(false);
    const [deleteError, setDeleteError] = useState(null);

    const fetchQuestions = useCallback(async () => {
        try {
            setLoading(true);
            const response = await getReviewQuestions();
            if (response && response.success) {
                setQuestions(response.data);
            } else {
                showSnackbar(response?.message || "Failed to load review questions.", "error");
            }
        } catch (err) {
            showSnackbar("Failed to fetch review questions. Please try again later.", "error");
        } finally {
            setLoading(false);
        }
    }, [showSnackbar]);

    useEffect(() => {
        fetchQuestions();
    }, [fetchQuestions]);

    const handleOpenModal = (item = null) => {
        setFieldErrors({});
        if (item) {
            setIsEdit(true);
            setCurrentItem(item);
            setFormData({
                question: item.question,
                example: item.example
            });
        } else {
            setIsEdit(false);
            setCurrentItem(null);
            setFormData({
                question: '',
                example: ''
            });
        }
        setOpenModal(true);
    };

    const handleCloseModal = () => {
        setOpenModal(false);
        setIsEdit(false);
        setCurrentItem(null);
        setFormData({
            question: '',
            example: ''
        });
        setFieldErrors({});
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
        if (fieldErrors[name]) {
            setFieldErrors(prev => ({
                ...prev,
                [name]: ''
            }));
        }
    };

    const handleSubmit = async () => {
        const errors = {};
        if (!formData.question.trim()) {
            errors.question = "Question is required";
        }
        if (!formData.example.trim()) {
            errors.example = "Example is required";
        }

        if (Object.keys(errors).length > 0) {
            setFieldErrors(errors);
            return;
        }
        setFieldErrors({});

        setSubmitting(true);

        try {
            if (isEdit) {
                await updateReviewQuestion(currentItem.id, formData);
                showSnackbar("Review question updated successfully!", "success");
            } else {
                await createReviewQuestion(formData);
                showSnackbar("Review question created successfully!", "success");
            }

            handleCloseModal();
            fetchQuestions();
        } catch (err) {
            console.error(err);
            const serverError = err.response?.data;
            const errMsg = serverError?.errors?.[0] || 
                           serverError?.details?.[0]?.message || 
                           serverError?.message || 
                           err.message || 
                           `Failed to ${isEdit ? 'update' : 'create'} review question`;
            
            showSnackbar(errMsg, "error");

            const errorsObj = {};
            if (serverError && serverError.details && Array.isArray(serverError.details)) {
                serverError.details.forEach(item => {
                    const fieldName = item.field === 'general' ? 'question' : item.field;
                    errorsObj[fieldName] = item.message;
                });
                setFieldErrors(errorsObj);
            } else if (serverError && serverError.errors && Array.isArray(serverError.errors) && serverError.errors.length > 0) {
                errorsObj['question'] = serverError.errors[0];
                setFieldErrors(errorsObj);
            }
        } finally {
            setSubmitting(false);
        }
    };

    const handleDeleteClick = (id) => {
        setSelectedId(id);
        setDeleteError(null);
        setDeleteModalOpen(true);
    };

    const handleConfirmDelete = async () => {
        if (selectedId) {
            try {
                setIsDeleting(true);
                setDeleteError(null);
                const response = await deleteReviewQuestion(selectedId);
                if (response && response.success) {
                    setDeleteModalOpen(false);
                    setSelectedId(null);
                    showSnackbar("Review question deleted successfully!", "success");
                    fetchQuestions();
                } else {
                    setDeleteError(response?.message || "Failed to delete review question");
                }
            } catch (err) {
                console.error("Delete Error:", err);
                const errorMessage = err.response?.data?.message || err.message || "Failed to delete review question";
                setDeleteError(errorMessage);
            } finally {
                setIsDeleting(false);
            }
        }
    };

    return (
        <PermissionWrapper permission="view">
            <Box sx={{ p: { xs: 2, md: 4 }, bgcolor: '#FBFBFB', minHeight: '100vh' }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4, flexWrap: 'wrap', gap: 2 }}>
                    <Typography sx={{ color: '#222', fontSize: { xs: '1.75rem', md: '1.8rem' }, fontWeight: 500 }}>
                        Review Questions
                    </Typography>
                    <PermissionWrapper permission="create" mode="disable">
                        <Button
                            disableElevation disableFocusRipple disableRipple disableTouchRipple
                            variant="contained" startIcon={<Plus size={20} />}
                            onClick={() => handleOpenModal()}
                            sx={{ bgcolor: '#ffae42', px: 3, color: '#fff', borderRadius: '8px' }}
                        >
                            Add Question
                        </Button>
                    </PermissionWrapper>
                </Box>

            <Paper sx={{ borderRadius: "8px", border: "1px solid #E8E8E8", overflow: "hidden" }} elevation={0}>
                <TableContainer>
                    <Table sx={{ minWidth: 950 }}>
                        <TableHead>
                            <TableRow sx={{ bgcolor: '#F8FAFC', borderBottom: '2px solid #EEF2F6' }}>
                                <TableCell sx={{ fontWeight: 600, color: '#475569', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.6px', py: 1.5, px: 1.5, whiteSpace: 'nowrap', textAlign: 'center' }}>S.No</TableCell>
                                <TableCell sx={{ fontWeight: 600, color: '#475569', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.6px', py: 1.5, px: 1.5, whiteSpace: 'nowrap' }}>Question</TableCell>
                                <TableCell sx={{ fontWeight: 600, color: '#475569', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.6px', py: 1.5, px: 1.5, whiteSpace: 'nowrap' }}>Example</TableCell>
                                <TableCell sx={{ fontWeight: 600, color: '#475569', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.6px', py: 1.5, px: 1.5, whiteSpace: 'nowrap', width: '130px' }}>Created By</TableCell>
                                <TableCell sx={{ fontWeight: 600, color: '#475569', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.6px', py: 1.5, px: 1.5, whiteSpace: 'nowrap', width: '130px' }}>Updated By</TableCell>
                                <TableCell sx={{ fontWeight: 600, color: '#475569', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.6px', py: 1.5, px: 1.5, whiteSpace: 'nowrap', width: '120px' }}>Created At</TableCell>
                                <TableCell sx={{ fontWeight: 600, color: '#475569', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.6px', py: 1.5, px: 1.5, whiteSpace: 'nowrap', width: '120px' }}>Updated At</TableCell>
                                <TableCell sx={{ fontWeight: 600, color: '#475569', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.6px', py: 1.5, px: 1.5, whiteSpace: 'nowrap' }}>Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {loading ? (
                                <TableRow>
                                    <TableCell colSpan={8} align="center" sx={{ py: 10 }}>
                                        <CircularProgress sx={{ color: '#FFBD65' }} />
                                    </TableCell>
                                </TableRow>
                            ) : questions.length > 0 ? (
                                questions.map((item, idx) => (
                                    <TableRow key={item.id} sx={{ '& td': { borderBottom: '1px solid #F0F0F0' } }}>
                                        <TableCell sx={{ color: '#666', textAlign: 'center', py: 1.25, px: 1.5, fontSize: '0.8rem' }}>{idx + 1}</TableCell>
                                        <TableCell sx={{ color: '#1E293B', fontWeight: 500, py: 1.25, px: 1.5, fontSize: '0.8rem' }}>
                                            {item.question}
                                        </TableCell>
                                        <TableCell sx={{ color: '#64748B', py: 1.25, px: 1.5, fontSize: '0.8rem' }}>
                                            {item.example}
                                        </TableCell>
                                        <TableCell sx={{ py: 1.25, px: 1.5, fontSize: '0.8rem', color: '#64748B', whiteSpace: 'nowrap' }}>
                                            {item.cuName || '-'}
                                        </TableCell>
                                        <TableCell sx={{ py: 1.25, px: 1.5, fontSize: '0.8rem', color: '#64748B', whiteSpace: 'nowrap' }}>
                                            {item.uuName || '-'}
                                        </TableCell>
                                        <TableCell sx={{ py: 1.25, px: 1.5, fontSize: '0.8rem', color: '#64748B', whiteSpace: 'nowrap' }}>
                                            {formatDate(item.createdAt)}
                                        </TableCell>
                                        <TableCell sx={{ py: 1.25, px: 1.5, fontSize: '0.8rem', color: '#64748B', whiteSpace: 'nowrap' }}>
                                            {formatDate(item.updatedAt)}
                                        </TableCell>
                                        <TableCell sx={{ py: 1.25, px: 1.5 }}>
                                            <Stack direction="row" spacing={1}>
                                                <PermissionWrapper permission="edit" mode="disable">
                                                    <Tooltip title="Edit">
                                                        <IconButton disableFocusRipple disableRipple disableTouchRipple size="small" sx={{ color: '#444' }} onClick={() => handleOpenModal(item)}>
                                                            <SquarePen size={18} />
                                                        </IconButton>
                                                    </Tooltip>
                                                </PermissionWrapper>
                                                <PermissionWrapper permission="delete" mode="disable">
                                                    <Tooltip title="Delete">
                                                        <IconButton disableFocusRipple disableRipple disableTouchRipple size="small" sx={{ color: '#FF4D49' }} onClick={() => handleDeleteClick(item.id)}>
                                                            <Trash2 size={18} />
                                                        </IconButton>
                                                    </Tooltip>
                                                </PermissionWrapper>
                                            </Stack>
                                        </TableCell>
                                    </TableRow>
                                ))
                            ) : (
                                <TableRow>
                                    <TableCell colSpan={8} align="center" sx={{ py: 3, color: '#64748B', fontSize: '0.8rem' }}>
                                        No review questions found.
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Paper>

            <Dialog open={openModal} onClose={handleCloseModal} maxWidth="sm" fullWidth>
                <DialogTitle sx={{ fontWeight: 600, color: '#222' }}>
                    {isEdit ? 'Edit Review Question' : 'Add Review Question'}
                </DialogTitle>
                <DialogContent>
                    <Stack spacing={3} sx={{ mt: 1 }}>
                        <TextField
                            fullWidth
                            label="Question *"
                            name="question"
                            value={formData.question}
                            onChange={handleInputChange}
                            placeholder="e.g. How was your experience?"
                            autoFocus
                            multiline
                            rows={2}
                            error={Boolean(fieldErrors.question)}
                            helperText={fieldErrors.question}
                        />
                        <TextField
                            fullWidth
                            label="Example *"
                            name="example"
                            value={formData.example}
                            onChange={handleInputChange}
                            placeholder="e.g. It was excellent!"
                            multiline
                            rows={2}
                            error={Boolean(fieldErrors.example)}
                            helperText={fieldErrors.example}
                        />
                    </Stack>
                </DialogContent>
                <DialogActions sx={{ p: 3 }}>
                    <Button onClick={handleCloseModal} sx={{ color: '#222' }}>Cancel</Button>
                    <Button disableElevation disableFocusRipple disableRipple disableTouchRipple
                        onClick={handleSubmit} disabled={submitting} variant="contained"
                        sx={{ bgcolor: '#ffae42', color: '#fff' }}
                    >
                        {submitting ? <CircularProgress size={24} color="inherit" /> : (isEdit ? 'Update' : 'Add')}
                    </Button>
                </DialogActions>
            </Dialog>

            <DeleteModal
                open={deleteModalOpen}
                onClose={() => setDeleteModalOpen(false)}
                onDelete={handleConfirmDelete}
                title="Delete Review Question"
                description="Are you sure you want to delete this review question? This action cannot be undone."
                error={deleteError}
                loading={isDeleting}
            />
            </Box>
        </PermissionWrapper>
    );
};

export default ReviewQuestions;
